import { mergeJSXIntoNode } from "@builder/store/slices/BuilderSlice/builderSlice";
import { getLayer, getAllDescendantsOfNode, isValidParent } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { convertJSXtoNodes } from "@builder/utils/JSXtoBuilder";
import { isJSX } from "@builder/utils/JSXtoBuilder/jsxHelpers";
import { healJSX } from "@builder/utils/JSXtoBuilder/healbrokenJSX";
import store from "@builder/store/store";
import type { RootState } from "@builder/store/store";

export function validateAndMergeJSX({
    jsx: initialJSX,
    targetLayerId,
    targetElement
}: {
    jsx: string;
    targetLayerId: string;
    targetElement?: string;
}): void {
    const state: RootState = store.getState();
    const builderState = state.builder;
    const cvMode = builderState.conditionalVariationMode;
    let jsx = initialJSX;
    if (!isJSX(jsx)) {
        try {
            const healed = healJSX(jsx);
            if (!isJSX(healed)) {
                throw new Error();
            }
            console.info("healJSX succeeded – using healed JSX");
            jsx = healed;
        } catch {
            throw new Error("Provided string is not valid JSX and could not be healed");
        }
    }

    const baseLayer = getLayer(builderState, targetLayerId);
    if (!baseLayer) {
        throw new Error("Base layer not found for the provided layerId");
    }

    const targetElementId = targetElement || baseLayer.root;
    const targetElementNode = baseLayer.components[targetElementId];
    if (!targetElementNode) {
        throw new Error("Target element not found in layer components");
    }

    // Structural validation
    if (!targetElementNode.parent) {
        const descendants = getAllDescendantsOfNode(targetElementNode, Object.values(baseLayer.components));
        const { rootNode, allNodes } = convertJSXtoNodes(jsx, targetElementId, {
            fromNodes: descendants,
            canThrowError: true
        });
        if (!rootNode || allNodes.length === 0) {
            throw new Error("JSX conversion produced no nodes (root merge)");
        }
    } else {
        const targetParentNode = baseLayer.components[targetElementNode.parent];
        const descendants = getAllDescendantsOfNode(targetParentNode, Object.values(baseLayer.components));
        const { rootNode, allNodes } = convertJSXtoNodes(jsx, targetElementNode.parent, {
            fromNodes: [...descendants, targetElementNode],
            canThrowError: true
        });
        if (!rootNode || allNodes.length === 0) {
            throw new Error("JSX conversion produced no nodes (non-root merge)");
        }
        if (!isValidParent(baseLayer, rootNode, targetParentNode.id, undefined, !!cvMode?.id)) {
            throw new Error("Invalid parent for root node produced by JSX");
        }
    }

    store.dispatch(mergeJSXIntoNode({ jsx, targetLayerId, targetElement }));
}
